import { useEffect, useMemo, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { CalendarIcon, DotsVerticalIcon } from "@radix-ui/react-icons";
import { format } from "date-fns";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import * as z from "zod";

import { marketingEventsSubtypes } from "src/components/Analytics/MarketingActivity/MarketingActivity";
import { Badge } from "src/components/ui/badge";
import { Button } from "src/components/ui/button";
import { Calendar } from "src/components/ui/calendar";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "src/components/ui/form";
import { Input } from "src/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "src/components/ui/popover";
import {
  ResponsiveDialog,
  ResponsiveDialogClose,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from "src/components/ui/responsive-dialog";
import { ScrollArea, ScrollBar } from "src/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import { Textarea } from "src/components/ui/textarea";
import { useApiMutation } from "src/lib/hooks/useApi";
import { useIsMobile } from "src/lib/hooks/useMobile";
import {
  CREATE_INTEGRATION_EVENT_MUTATION,
  DELETE_INTEGRATION_EVENT_MUTATION,
  UPDATE_INTEGRATION_EVENT_MUTATION,
} from "src/lib/queries/marketingActivity";
import { cn } from "src/lib/utils";

const formatDate = (date) => format(date, "PP");

const eventFormSchema = z.object({
  subtype: z.string().min(1, { message: "Event type is required" }),
  name: z.string().min(1, { message: "Event name is required" }),
  description: z.string().optional(),
  date: z
    .object({
      from: z.date({ required_error: "Start date is required" }),
      to: z.date().optional(),
    })
    .refine(
      (data) => {
        if (data.to && data.from) {
          return data.to >= data.from;
        }
        return true;
      },
      {
        message: "End date must be after start date",
        path: ["date"], // error appears on the `to` field
      }
    ),
});

export function MarketingEventDialog({
  open,
  setOpen,
  integrationId,
  data,
  onUpdate,
  loading: _loading,
}) {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const mutationMode = selectedEvent
    ? selectedEvent?.id
      ? "edit"
      : "create"
    : null;

  const [eventFormLoading, setEventFormLoading] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);

  const loading = eventFormLoading || _loading;

  // Content shown when no event is selected
  const eventListContent = useMemo(() => {
    if (selectedEvent) {
      return null;
    }

    return data?.events?.map((event, index) => {
      const days = Math.ceil(
        (new Date(event.dateTo) - new Date(event.dateFrom)) /
          (1000 * 60 * 60 * 24)
      );

      const Icon = marketingEventsSubtypes[event.subtype]?.icon;
      return (
        <button
          key={index}
          onClick={() => {
            setSelectedEvent(event);
          }}
          disabled={loading}
          className={cn(
            "relative bg-card hover:bg-card/80 flex flex-col justify-between gap-y-4 rounded-lg border p-4 transition-colors",
            loading ? "opacity-50" : "cursor-pointer"
          )}
        >
          <DotsVerticalIcon className="absolute top-2 right-2" />

          <div className="flex gap-2 items-center">
            <Badge variant="secondary">
              {Icon && <Icon className="size-4 mr-1" />}
              {marketingEventsSubtypes[event.subtype]?.label ||
                event.subtype ||
                "Event"}
            </Badge>
            <span className="text-muted-foreground text-sm">
              {formatDate(event.dateFrom)} - {formatDate(event.dateTo)} ({days}{" "}
              {days === 1 ? "day" : "days"})
            </span>
          </div>
          <span className="font-semibold text-left">
            {event.name || "Marketing Event"}
          </span>
          {event.description && (
            <span className="text-muted-foreground text-left text-sm">
              {event.description}
            </span>
          )}
        </button>
      );
    });
  }, [selectedEvent, data?.events, loading]);

  return (
    <>
      <ResponsiveDialog
        open={open}
        onOpenChange={(state) => {
          if (!state) {
            setSelectedEvent(null);
          }
          setOpen(state);
        }}
      >
        <ResponsiveDialogContent
          className={cn("flex flex-col", !selectedEvent && "h-full sm:h-auto")}
        >
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle className="items-baseline">
              {selectedEvent
                ? mutationMode === "edit"
                  ? "Edit Event"
                  : "Create Event"
                : "Marketing Events"}

              {data?.dateFrom && (
                <span className="text-muted-foreground ml-2 text-sm font-normal">
                  {formatDate(data.dateFrom)}
                  {data.dateTo && <> - {formatDate(data.dateTo)}</>}
                </span>
              )}
            </ResponsiveDialogTitle>
            {!loading && !selectedEvent && !data.events?.length && (
              <ResponsiveDialogDescription>
                <span className="my-8 block w-full text-center">
                  No Marketing Events
                </span>
              </ResponsiveDialogDescription>
            )}
          </ResponsiveDialogHeader>

          <ScrollArea className="-mr-3 pr-3 flex flex-col">
            <div className="flex flex-col space-y-4 p-px sm:max-h-[60vh]">
              {eventListContent}

              {selectedEvent && (
                <EventForm
                  event={selectedEvent}
                  integrationId={integrationId}
                  setLoading={setEventFormLoading}
                  afterSubmit={() => {
                    setSelectedEvent(null);
                    setEventFormLoading(false);

                    onUpdate?.();
                  }}
                />
              )}
            </div>

            <ScrollBar />
          </ScrollArea>

          <ResponsiveDialogFooter className="flex-row flex-wrap">
            {mutationMode === "edit" && (
              <Button
                className="sm:mr-auto"
                variant="destructive"
                disabled={loading}
                onClick={() => {
                  setEventToDelete(selectedEvent);
                }}
              >
                Delete
              </Button>
            )}

            <ResponsiveDialogClose
              asChild
              onClick={(e) => {
                if (selectedEvent) {
                  e.preventDefault();
                  setSelectedEvent(null);
                }
              }}
            >
              <Button
                variant="secondary"
                disabled={loading}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
            </ResponsiveDialogClose>

            {selectedEvent && (
              <Button
                className="grow sm:grow-0"
                form="event-form"
                type="submit"
                disabled={loading}
              >
                Save
              </Button>
            )}

            {!selectedEvent && (
              <Button
                className="w-full sm:ml-auto sm:w-auto"
                disabled={loading}
                onClick={() => {
                  setSelectedEvent({
                    dateFrom: data?.dateFrom,
                    dateTo: data?.dateTo || data?.dateFrom,
                  });
                }}
              >
                Create Event
              </Button>
            )}
          </ResponsiveDialogFooter>
        </ResponsiveDialogContent>
      </ResponsiveDialog>

      <EventDeleteDialog
        event={eventToDelete}
        integrationId={integrationId}
        onClose={() => {
          setEventToDelete(null);
        }}
        onDelete={() => {
          setSelectedEvent(null);
          onUpdate?.(); // update parent component
        }}
      />
    </>
  );
}

function EventForm({ event, integrationId, setLoading, afterSubmit }) {
  const isMobile = useIsMobile();
  const form = useForm({
    resolver: zodResolver(eventFormSchema),
    defaultValues: {
      subtype: event?.subtype || "",
      name: event?.name || "",
      description: event?.description || "",
      date: {
        from: event?.dateFrom ? new Date(event.dateFrom) : new Date(),
        to: event?.dateTo ? new Date(event.dateTo) : new Date(),
      },
    },
  });
  const { mutationApi: createEvent, loading: createEventLoading } =
    useApiMutation({
      mutation: CREATE_INTEGRATION_EVENT_MUTATION,
    });
  const { mutationApi: updateEvent, loading: updateEventLoading } =
    useApiMutation({
      mutation: UPDATE_INTEGRATION_EVENT_MUTATION,
    });

  const loading = createEventLoading || updateEventLoading;

  useEffect(() => {
    if (setLoading) {
      setLoading(loading);
    }
  }, [loading, setLoading]);

  const onSubmit = async (data) => {
    const toastId = toast.loading(
      event?.id ? "Updating event..." : "Creating event..."
    );

    const dateFrom =
      data.date.from.toLocaleDateString("en-CA").slice(0, 10) +
      "T00:00:00.000Z";
    const dateTo =
      (data.date.to || data.date.from)
        .toLocaleDateString("en-CA")
        .slice(0, 10) + "T23:59:59.999Z";

    const { data: res } = await (event?.id ? updateEvent : createEvent)({
      id: event?.id,
      input: {
        ...(!event?.id && { integrationId, type: "MARKETING_ACTIVITY" }),
        ...data,
        date: undefined, // we handle dateFrom and dateTo separately
        dateFrom,
        dateTo,
      },
    });

    if (res) {
      toast.success(
        event?.id
          ? "Event updated successfully!"
          : "Event created successfully!",
        { id: toastId }
      );

      afterSubmit?.();
    } else {
      toast.error("Failed to save event", { id: toastId });
    }
  };

  return (
    <Form {...form}>
      <form
        id="event-form"
        className="space-y-6"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <FormField
          control={form.control}
          name="subtype"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Type</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Event type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {Object.entries(marketingEventsSubtypes).map(
                    ([key, subtype]) => {
                      const Icon = subtype.icon;
                      return (
                        <SelectItem value={key} key={key}>
                          <div className="flex items-center gap-2">
                            {Icon && <Icon className="size-4" />}
                            {subtype.label}
                          </div>
                        </SelectItem>
                      );
                    }
                  )}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="name"
          disabled={loading}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Enter event name" />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          disabled={loading}
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Description{" "}
                <span className="text-muted-foreground">(optional)</span>
              </FormLabel>
              <FormControl>
                <Textarea {...field} placeholder="Enter event description" />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="date"
          render={({ field }) => (
            <FormItem className="flex w-full flex-col">
              <FormLabel>Event Date</FormLabel>
              <Popover modal={true}>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full pl-3 text-left font-normal",
                        !field.value?.from && "text-muted-foreground"
                      )}
                    >
                      {field.value?.from ? (
                        formatDate(field.value.from) +
                        (field.value?.to && field.value.to !== field.value.from
                          ? ` - ${formatDate(field.value.to)}`
                          : "")
                      ) : (
                        <span>Pick a date range</span>
                      )}
                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="range"
                    defaultMonth={field.value?.from}
                    selected={field.value}
                    onSelect={(value) => {
                      field.onChange(
                        value || {
                          from: event.dateFrom || new Date(),
                          to: event.dateTo || new Date(),
                        }
                      );
                    }}
                    className="rounded-lg border shadow-sm"
                    captionLayout="dropdown"
                    numberOfMonths={isMobile ? 1 : 2}
                    weekStartsOn={1}
                  />
                </PopoverContent>
              </Popover>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
}

function EventDeleteDialog({ event, onClose, onDelete }) {
  const { mutationApi: deleteEvent, loading } = useApiMutation({
    mutation: DELETE_INTEGRATION_EVENT_MUTATION,
    onSuccess: () => {
      onClose?.();
      onDelete?.();
    },
    onError: () => "Failed to delete event",
  });

  const handleDelete = async () => {
    const toastId = toast.loading(`Deleting event "${event.name}"`);

    const { data } = await deleteEvent({
      id: event.id,
    });

    if (data) {
      toast.success(`Event "${event.name}" deleted successfully`, {
        id: toastId,
      });
    } else {
      toast.error(`Failed to delete event "${event.name}"`, {
        id: toastId,
      });
    }
  };

  return (
    <ResponsiveDialog open={!!event} onOpenChange={onClose}>
      <ResponsiveDialogContent className="h-auto">
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>Delete Marketing Event</ResponsiveDialogTitle>
          <ResponsiveDialogDescription>
            Are you sure you want to delete the event &quot;
            {event?.name}&quot;? This action cannot be undone.
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>
        <ResponsiveDialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={loading}
          >
            Delete
          </Button>
        </ResponsiveDialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
